import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { getIn } from "formik";
import { useCustomTranslationHandler, useFontSize, useGetAppLanguage, useIconSize, useWindowDimetions } from "@app/hooks";
import type { SelectInstance, SingleValue, MultiValue, OptionProps } from "react-select";
import type { FC } from "react";
import type { DropdownOptions, Option, TranslationKey } from "@typedefs/modulesInterfaces";
import type { FieldProps } from "formik";
import Select from "react-select";
import makeAnimated from "react-select/animated";

// Constants
const DELETE_ENTRY_ID = -1;
const LOAD_MORE_ID = -2;
const FONT_FAMILY = "AIAIAIv4 normal";
const FONT_FAMILY_MEDIUM = "AIAIAIv4 medium";

const deleteEntryOption: Option = {
  id: DELETE_ENTRY_ID,
  name: "Delete Entry",
  name_en: "Delete Entry",
  name_fr: "Supprimer l'entrée",
  name_es: "Eliminar Entrada",
  name_ru: "Удалить запись",
};

const loadMoreOption: Option = {
  id: LOAD_MORE_ID,
  name: "Load more",
  name_en: "Load more",
  name_fr: "Charger plus",
  name_es: "Cargar más",
  name_ru: "Загрузить еще",
};

interface CustomSelectProps {
  label: TranslationKey;
  type: "text" | "password" | "select";
  options: DropdownOptions;
  classes?: string;
  disabled?: boolean;
  autosubmit?: boolean;
  onChange?: (e: any) => void;
  labelSuffixValue?: string;
  isMulti?: boolean;
  handleLoadMore?: () => void;
}

interface ReadOnlyOption {
  readonly label: string;
  readonly value: number;
}

// Utility functions
const selectOption = (option: Option): ReadOnlyOption => ({
  label: option.name,
  value: option.id,
});

const DELETE_OPTION = selectOption(deleteEntryOption);
const LOAD_MORE_OPTION = selectOption(loadMoreOption);

const createFormEvent = (fieldName: string, value: any) => ({
  persist: () => { },
  target: {
    type: "change" as const,
    id: fieldName,
    name: fieldName,
    value,
  },
});

const getBorderColor = (state: any, error: string | undefined, wasTouched: boolean) => {
  if (state.isDisabled) return "#999999";
  if (state.isFocused) return "var(--dark-gray)";
  if (error && wasTouched) return "var(--error)";
  return "var(--gray)";
};

// Custom Load More Option Component
const LoadMoreOption: FC<OptionProps<ReadOnlyOption, boolean>> = ({ innerProps, children }) => (
  <div
    {...innerProps}
    style={{
      padding: '8px 12px',
      cursor: 'pointer',
      backgroundColor: 'var(--white)',
      borderTop: '1px solid var(--gray)',
      color: 'var(--dark-gray)',
      fontWeight: 500,
      textAlign: 'center',
      fontSize: "var(--fs-14)",
    }}
  >
    {children}
  </div>
);

const CustomSelectInput: FC<CustomSelectProps & FieldProps> = ({
  label,
  field,
  form,
  type,
  options,
  classes,
  disabled,
  form: { touched, errors, setTouched },
  autosubmit,
  onChange,
  labelSuffixValue,
  isMulti = false,
  handleLoadMore,
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [ddOptions, setDdOptions] = useState<ReadOnlyOption[]>([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const selectRef = useRef<SelectInstance<ReadOnlyOption> | null>(null);

  const error = getIn(errors, field.name);
  const wasTouched = getIn(touched, field.name);
  const { apiLanguageField } = useGetAppLanguage();
  const translationHandler = useCustomTranslationHandler();

  // Memoized values
  const fsWithValue = useFontSize(12);
  const fsWithoutValue = useFontSize(14);
  const { width } = useWindowDimetions();
	const transformValue = width > 2560 ? "translateY(10px)" : "translateY(5px)";


  const labelEl = useCallback((hasValue: boolean) => ({
    ":before": {
      content: `"${translationHandler(label)}${labelSuffixValue ? ` - ${labelSuffixValue}` : ""}"`,
      transform: `translateY(${hasValue ? "-5px" : "3px"})`,
      display: "block",
      fontSize: hasValue ? fsWithValue : fsWithoutValue,
      color: "var(--dark-gray)",
      position: "absolute" as const,
      top: 8,
      left: 10,
      zIndex: 100,
      transition: "transform 0.2s ease",
    },
  }), [translationHandler, label, labelSuffixValue]);

  const selectStyles = useMemo(() => ({
    indicatorSeparator: () => ({
      display: "none",
    }),
    container: (baseStyles: any) => ({
      ...baseStyles,
      width: "100%",
      height: "var(--h-36)",
      fontFamily: FONT_FAMILY,
    }),
    singleValue: (baseStyles: any) => ({
      ...baseStyles,
      fontWeight: 500,
    }),
    input: (baseStyles: any) => ({
      ...baseStyles,
    }),
    placeholder: (baseStyles: any) => ({
      ...baseStyles,
      display: "none"
    }),
    control: (baseStyles: any, state: any) => ({
      ...baseStyles,
      border: `1px solid ${getBorderColor(state, error, wasTouched)} !important`,
      borderRadius: 6,
      backgroundColor: state.isDisabled ? "#eeeeee" : "var(--white)",
      boxShadow: "none",
      height: "100%",
      ...labelEl(state.hasValue || state.menuIsOpen),
    }),
    valueContainer: (baseStyles: any, state: any) => ({
      ...baseStyles,
      color: "var(--gray)",
      fontSize: "var(--fs-11)",
      fontFamily: state.hasValue ? FONT_FAMILY_MEDIUM : FONT_FAMILY,
      transform: `${state.hasValue ? transformValue : "none"}`,
    }),
    menuPortal: (baseStyle: any) => ({ ...baseStyle, zIndex: 9999 }),
    menu: (baseStyles: any) => ({
      ...baseStyles,
      background: "white",
      minWidth: 100,
      borderRadius: 8,
      outline: 0,
      border: "1px solid var(--dark-gray)",
      fontSize: "var(--fs-14)",
    }),
    option: (baseStyles: any, state: any) => ({
      ...baseStyles,
      background: state.isFocused || state.isSelected ? "#eee" : "transparent",
      color: state.data.value === DELETE_ENTRY_ID
        ? "var(--error)"
        : state.data.value === LOAD_MORE_ID
          ? "var(--dark-gray)"
          : state.isSelected || state.isFocused
            ? "black"
            : baseStyles.color,
    }),
  }), [error, wasTouched, labelEl]);

  // Effects
  useEffect(() => {
    const getOptions = async () => {
      setIsLoading(true);
      try {
        const rawOptions: Option[] = typeof options === "function"
          ? (await options()).data
          : options || [];

        const translatedOptions = rawOptions.map((option) => ({
          ...option,
          name: option[apiLanguageField] ?? option.name,
        }));

        setDdOptions(translatedOptions.map(selectOption));
      } catch (error) {
        console.error('Error loading options:', error);
        setDdOptions([]);
      } finally {
        setIsLoading(false);
      }
    };

    getOptions();
  }, [options, apiLanguageField]);

  // Event handlers
  const handleSelect = useCallback((
    newValue: SingleValue<ReadOnlyOption> | MultiValue<ReadOnlyOption>
  ) => {
    if (isMulti) {
      const values = (newValue as MultiValue<ReadOnlyOption>) || [];
      const eventValue = values
        .map(v => v.value === DELETE_ENTRY_ID ? null : v.value)
        .filter(Boolean);

      const event = createFormEvent(field.name, eventValue);
      field.onChange(event);
      onChange?.(event);
    } else {
      const singleValue = newValue as SingleValue<ReadOnlyOption>;
      if (singleValue?.value !== undefined) {
        // Handle Load More option
        if (singleValue.value === LOAD_MORE_ID) {
          handleLoadMore?.();
          return;
        }

        const eventValue = singleValue.value === DELETE_ENTRY_ID ? null : singleValue.value;
        const event = createFormEvent(field.name, eventValue);
        field.onChange(event);
        onChange?.(event);
      }
    }
  }, [field, onChange, isMulti, handleLoadMore]);

  const handleFocus = useCallback(() => {
    setTouched({ [field.name]: true });
  }, [setTouched, field.name]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !isMenuOpen) {
      // Only intercept Enter when menu is closed to open it
      event.preventDefault();
      event.stopPropagation();
      selectRef.current?.openMenu('first');
    }
    // When menu is open, let react-select handle Enter naturally (it will select the highlighted option)
  }, [isMenuOpen]);

  const handleMenuOpen = useCallback(() => {
    setIsMenuOpen(true);
  }, []);

  const handleMenuClose = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  // Computed values
  const selectOptions = useMemo(() => {
    let options = ddOptions;

    // Add delete option for single select with value
    if (!isMulti && field.value) {
      options = [DELETE_OPTION, ...options];
    }

    // Add load more option if handleLoadMore is provided
    if (handleLoadMore) {
      options = [...options, LOAD_MORE_OPTION];
    }

    return options;
  }, [isMulti, field.value, ddOptions, handleLoadMore]);

  const selectValue = useMemo(() => {
    if (isMulti) {
      return ddOptions?.filter(option => field.value?.includes(option.value));
    }
    return ddOptions?.find((option) => option.value === field.value) || null;
  }, [isMulti, ddOptions, field.value]);

  // Custom components
  const customComponents = useMemo(() => {
    const baseComponents = makeAnimated();

    if (handleLoadMore) {
      return {
        ...baseComponents,
        Option: ({ data, ...props }: OptionProps<ReadOnlyOption, boolean>) => {
          if (data.value === LOAD_MORE_ID) {
            return <LoadMoreOption {...props} data={data}>{data.label}</LoadMoreOption>;
          }
          return baseComponents.Option ? baseComponents.Option({ data, ...props }) : null;
        },
      };
    }

    return baseComponents;
  }, [handleLoadMore]);

  return (
    <Select
      ref={selectRef}
      isMulti={isMulti}
      components={customComponents}
      isDisabled={isLoading || disabled}
      isLoading={isLoading}
      onChange={handleSelect}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      onMenuOpen={handleMenuOpen}
      onMenuClose={handleMenuClose}
      options={selectOptions}
      value={selectValue}
      menuPortalTarget={document.body}
      styles={selectStyles}
      {...props}
      className="fs-11"
    />
  );
};

export default CustomSelectInput;